import type { MaskKey, VariableEnumValue, VariableType } from '@/shared/variables/core/types'

/**
 * Catálogo de COLECCIONES para el motor de iteración (loops).
 *
 * Una colección es una lista de registros (ej. los comparecientes). Dentro de un
 * loop, cada registro expone sus campos como variables con scope (ej.
 * `cliente.nombre`). Acá se definen los campos "lindos" (curados) de cada
 * colección y, para la fase DEMO, sus datos mock.
 *
 * NOTA (fase demo): los datos son mock para validar el motor SIN tocar el texto
 * legal real. En la fase de migración, `extraData` (IPdfTemplatePromise) se
 * adapta a este mismo formato de colecciones.
 */

// Un campo curado de un item: nombre corto + etiqueta + máscara sugerida.
export interface CollectionField {
  key: string // nombre corto dentro del item (ej. 'nombre', 'dpi_letras')
  label: string // etiqueta linda para el menú
  mask?: MaskKey // máscara sugerida del valor (opcional)
  type?: VariableType // tipo del campo (para operadores del condicional). Default 'string'.
  values?: VariableEnumValue[] // valores válidos si es 'enum' (para elegir sin escribir)
}

export interface CollectionCatalog {
  name: string // nombre de la colección (ej. 'clientes')
  defaultAlias: string // alias por defecto del item (ej. 'cliente')
  label: string // etiqueta para el menú
  fields: CollectionField[]
  demo: Array<Record<string, string>> // datos mock (un registro plano por item)
}

// Tipos de runtime de las colecciones. Importados para uso local Y reexportados desde
// el domain del engine: el valor de un campo puede ser string (escalar) o un array
// (colección anidada, 1 nivel). Los 11 importadores reciben el tipo ensanchado sin tocarse.
import type {
  CollectionRecord,
  Collections,
  CollectionValue
} from '@/document-engine/domain/schema/runtime'

export type { CollectionRecord, Collections, CollectionValue }

import { CATALOG_GENDER } from '@/shared/catalogs/gender'

/**
 * Campos virtuales del loop (helpers de índice), disponibles bajo cualquier alias
 * (ej. `cliente._letra`). Los inyecta el runtime del loop, no son datos del item.
 */
export const LOOP_INDEX_FIELDS: CollectionField[] = [
  { key: '_letra', label: 'Letra (A, B, C…)' },
  { key: '_posicion', label: 'Posición (1, 2, 3…)' },
  { key: '_indice', label: 'Índice (0, 1, 2…)' },
  { key: '_total', label: 'Total de items' },
  { key: '_primero', label: '¿Es el primero? (Sí/No)' },
  { key: '_ultimo', label: '¿Es el último? (Sí/No)' }
]

// Colección de demo: "clientes" (comparecientes). Los campos imitan los del
// IndividualPerson real (ya formateados: edad y DPI en letras, país por código).
export const CLIENTES_COLLECTION: CollectionCatalog = {
  name: 'clientes',
  defaultAlias: 'cliente',
  label: 'Clientes (comparecientes)',
  fields: [
    { key: 'nombre', label: 'Nombre' },
    { key: 'edad', label: 'Edad (en letras)' },
    {
      key: 'genero',
      label: 'Género',
      type: 'enum',
      values: CATALOG_GENDER // catálogo canónico (mismo que los formularios), no hardcode
    },
    { key: 'estado_civil', label: 'Estado civil' },
    { key: 'nacionalidad', label: 'Nacionalidad' },
    { key: 'profesion', label: 'Profesión' },
    { key: 'pais', label: 'País (código)' },
    { key: 'departamento', label: 'Departamento' },
    { key: 'dpi_numeros', label: 'DPI (números)' },
    { key: 'dpi_letras', label: 'DPI (en letras)' },
    // Figura (cómo comparece): 'propia' | 'juridica' | 'gestor'
    {
      key: 'figura',
      label: 'Figura (cómo comparece)',
      type: 'enum',
      values: [
        { value: 'propia', label: 'En nombre propio' },
        { value: 'juridica', label: 'Persona jurídica (rep. legal)' },
        { value: 'gestor', label: 'Gestor de negocios' }
      ]
    },
    // Jurídica
    { key: 'cargo', label: 'Cargo (rep. legal)' },
    { key: 'entidad', label: 'Entidad' },
    { key: 'nombramiento_fecha', label: 'Nombramiento — fecha' },
    { key: 'notario_tratamiento', label: 'Notario — tratamiento (el/la)' },
    { key: 'notario_nombre', label: 'Notario — nombre' },
    { key: 'registro_numero', label: 'Registro — número' },
    { key: 'registro_folio', label: 'Registro — folio' },
    { key: 'registro_libro', label: 'Registro — libro' },
    // Gestor
    { key: 'gestor_palabra', label: 'Gestor/Gestora' },
    { key: 'beneficiario', label: 'Beneficiario (gestor)' }
  ],
  demo: [
    {
      nombre: 'JUAN CARLOS PÉREZ GARCÍA',
      edad: 'treinta y cinco',
      genero: 'M',
      estado_civil: 'casado',
      nacionalidad: 'guatemalteco',
      profesion: 'Ingeniero Civil',
      pais: 'GT',
      departamento: 'Guatemala',
      dpi_numeros: '1234 56789 0101',
      dpi_letras: 'uno dos tres cuatro, cinco seis siete ocho nueve, cero uno cero uno'
    },
    {
      nombre: 'MARÍA ELENA RODRÍGUEZ LÓPEZ',
      edad: 'cuarenta y dos',
      genero: 'F',
      estado_civil: 'soltera',
      nacionalidad: 'guatemalteca',
      profesion: 'Abogada y Notaria',
      pais: 'GT',
      departamento: 'Sacatepéquez',
      dpi_numeros: '9876 54321 0301',
      dpi_letras: 'nueve ocho siete seis, cinco cuatro tres dos uno, cero tres cero uno'
    },
    {
      nombre: 'JOHN MICHAEL SMITH',
      edad: 'cincuenta',
      genero: 'M',
      estado_civil: 'casado',
      nacionalidad: 'estadounidense',
      profesion: 'Empresario',
      pais: 'US',
      departamento: '',
      dpi_numeros: '4567 89012 3456',
      dpi_letras: 'cuatro cinco seis siete, ocho nueve cero uno dos, tres cuatro cinco seis'
    }
  ]
}

// Índice de colecciones de demo por nombre.
export const DEMO_COLLECTIONS: Record<string, CollectionCatalog> = {
  [CLIENTES_COLLECTION.name]: CLIENTES_COLLECTION
}

// Construye las colecciones de runtime (mock) para el preview del demo.
export const buildDemoCollections = (): Collections => {
  const out: Collections = {}
  for (const cat of Object.values(DEMO_COLLECTIONS)) out[cat.name] = cat.demo
  return out
}
